// QuizSection.js - 新しい統合版

import React, { useState, useCallback, useMemo, Suspense, lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ConfirmDialog } from '../../modals';

// 分割したコンポーネントのインポート
import {
  QuizOverview,
  QuizContentList,
  QuizAssignmentForm,

  ErrorAlert,
  ResponsiveModal,
  ResponsiveSubjectTabs,
  FullAreaLoading,
  OverlayLoading
} from '../index';

// 新しいカスタムフックのインポート
import { useQuizContent, useQuizAssignment, useQuizResults } from './hooks';

// 🎯 動的インポート（コード分割）
const QuizPlayer = lazy(() => import('../player/QuizPlayer'));
const ResultsModal = lazy(() => import('../UI/ResultsModal'));

// QuizSectionコンポーネント - 統合版
const QuizSection = ({ currentUser, selectedStudent, isTeacher }) => {
  
  // 基本状態管理
  const [activeSubject, setActiveSubject] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContentId, setModalContentId] = useState('');
  const [showOverview, setShowOverview] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // 新しいカスタムフックの使用
  const {
    contents,
    error: contentError,
    studentId,
    clearError: clearContentError,
    getFilteredContents,
    findContentById
  } = useQuizContent(currentUser, selectedStudent, isTeacher);

  const {
    isAssigning,
    selectedContentId,
    assignmentError,
    startAssigning,
    cancelAssigning,
    selectContent,
    executeAssignment,
    getConfirmationMessage
  } = useQuizAssignment(studentId, selectedStudent, contents);

  const {
    showResultsModal,
    resultsTitle,
    resultsError,
    memoizedResults,
    handleViewResults,
    closeResultsModal,
    clearResultsError
  } = useQuizResults(isTeacher, studentId, currentUser, selectedStudent);

  // エラー統合管理
  const error = contentError || assignmentError || resultsError;
  const clearError = useCallback(() => {
    clearContentError();
    clearResultsError();
  }, [clearContentError, clearResultsError]);

  // モーダル制御
  const handleOpenContent = useCallback((contentId) => {
    setModalContentId(contentId);
    setIsModalOpen(true);
    setShowOverview(true);
    setIsQuizActive(false);
  }, []);

  const handleStartQuiz = useCallback(() => {
    setShowOverview(false);
    setIsQuizActive(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setShowOverview(false);
    setIsQuizActive(false);
    setModalContentId('');
  }, []);

  // 割り当て処理
  const handleAssignQuiz = useCallback(async () => {
    const result = await executeAssignment();
    
    if (result.success) {
      setConfirmDialog({
        title: '成功',
        message: result.message,
        onConfirm: () => setConfirmDialog(null),
        onCancel: null,
        cancelButtonText: null
      });
    } else {
      setConfirmDialog({
        title: 'エラー',
        message: result.error,
        onConfirm: () => setConfirmDialog(null),
        onCancel: null,
        cancelButtonText: null
      });
    }
  }, [executeAssignment]);

  // 割り当て確認ダイアログ
  const showAssignmentConfirmation = useCallback(() => {
    setConfirmDialog({
      title: 'コンテンツの割り当て',
      message: getConfirmationMessage(),
      onConfirm: handleAssignQuiz,
      onCancel: () => setConfirmDialog(null)
    });
  }, [getConfirmationMessage, handleAssignQuiz]);

  // 科目でフィルタリングされたコンテンツ取得（useMemo で再計算削減）
  const filteredContents = useMemo(() => getFilteredContents(activeSubject), [getFilteredContents, activeSubject]);

  // 現在のコンテンツ詳細取得（useMemo 化）
  const currentContentDetails = useMemo(() => findContentById(modalContentId), [findContentById, modalContentId]);

  /* =============================
    Bodyスクロールロック管理
    ============================= */
  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('quiz-playing');
    } else {
      document.body.classList.remove('quiz-playing');
    }
    return () => {
      document.body.classList.remove('quiz-playing');
    };
  }, [isModalOpen]);

  return (
    <div className="space-y-6">
      {/* エラー表示 - 統一されたErrorAlertコンポーネント */}
      {error && (
        <ErrorAlert
          message={error}
          type="error"
          onRetry={clearError}
          retryText="再試行"
        />
      )}

      {/* タイトル部分 - モバイル最適化 */}
      <h2 className="text-lg sm:text-xl font-semibold text-purple-800 mb-4 line-clamp-2">
        {selectedStudent ? `${selectedStudent.name}のコンテンツ` : 
         currentUser.role === 'student' ? `${currentUser.name}のコンテンツ` : 
         '割り当てられたコンテンツ'}
      </h2>

      {/* 教師が生徒にクイズを割り当てるUI部分 */}
      <QuizAssignmentForm
        isTeacher={isTeacher}
        selectedStudent={selectedStudent}
        isAssigning={isAssigning}
        selectedContentId={selectedContentId}
        contents={contents}
        onStartAssigning={startAssigning}
        onCancelAssigning={cancelAssigning}
        onContentSelect={selectContent}
        onAssignQuiz={showAssignmentConfirmation}
      />

      {/* 科目選択ボタン - レスポンシブ版 */}
      <ResponsiveSubjectTabs
        activeSubject={activeSubject}
        onSubjectSelect={setActiveSubject}
        enableResponsive={true}
      />

      {/* 割り当てられたコンテンツの表示 */}
      <div className="mb-6">
        <QuizContentList
          assignedContentsDetails={filteredContents}
          activeSubject={activeSubject}
          isTeacher={isTeacher}
          studentId={studentId}
          onOpenContent={handleOpenContent}
          onViewResults={handleViewResults}
        />
      </div>

      {/* 結果表示モーダル */}
      {showResultsModal && (
        <Suspense fallback={<OverlayLoading message="結果を読み込み中..." />}> 
          <ResultsModal
            isOpen={showResultsModal}
            onClose={closeResultsModal}
            results={memoizedResults}
            contentTitle={resultsTitle}
          />
        </Suspense>
      )}

      {/* 確認ダイアログ */}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
          confirmButtonText={confirmDialog.confirmButtonText}
          cancelButtonText={confirmDialog.cancelButtonText}
        />
      )}

      {/* クイズモーダル部分 - レスポンシブ版 */}
      {isModalOpen && (
        <>
          {showOverview ? (
            // 概要モーダル - レスポンシブ対応
            <ResponsiveModal
              variant="overview"
              enableResponsive={true}
              onBackgroundClick={closeModal}
            >
              <QuizOverview
                content={currentContentDetails}
                onStart={handleStartQuiz}
                onClose={closeModal}
              />
            </ResponsiveModal>
          ) : isQuizActive ? (
            // QuizPlayerをモーダルラップで全画面表示
            <div className="fixed inset-0 bg-white z-[9999] flex flex-col overflow-y-auto">
              <Suspense fallback={<FullAreaLoading message="クイズを読み込み中..." />}> 
                <QuizPlayer
                  modalContentId={modalContentId}
                  activeSubject={activeSubject}
                  isModalOpen={isModalOpen}
                  studentId={studentId}
                  contentDetails={currentContentDetails}
                  onClose={closeModal}
                />
              </Suspense>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

QuizSection.propTypes = {
  currentUser: PropTypes.object.isRequired,
  selectedStudent: PropTypes.object,
  isTeacher: PropTypes.bool.isRequired,
};

export default QuizSection; 